import { t } from "@i18n";
import { observer } from "@legendapp/state/react";
import { CellProps } from "react-table";

import { useRemotePaging } from "_hooks/useMakeRemoteFilter";

import { default as _Table } from "_components/Table";
import { Buttons, ViewIconButton } from "_ui/Button";

import { useAttendanceSearch } from "_api/hooks/attendances";
import { Perms } from "_enums/Perms";
import { Attendance } from "_types/models/Attendance";
import { ConferenceDesk } from "_types/models/ConferenceDesk";
import { Avatar } from "_ui/Avatar";
import { HStack } from "_ui/Box";
import { iso2view } from "_utils/date";
import { hasPerm } from "_utils/perms";
import { diff2time } from "_utils/time";
import { useRelativeGo } from "_utils/url";
import { optional } from "_utils/view";
import { $onFilterChange } from "../Filters";
import { Suspense } from "react";
import TableSuspense from "_components/suspense/TableSuspense";
import { AttendanceSituationLabels } from "_enums/AttendanceSituation";

const actions = ({ value, row: { original: conferenceDesk } }: CellProps<ConferenceDesk>) => {
    const go = useRelativeGo();

    // const edit = (id: string) => {
    //     go(E.url.edit(id));
    // }
    //
    const view = (id: string) => {
        go(id, 'view');
    }
    return (
        <Buttons>
            <ViewIconButton onClick={() => view(value)} tooltip='Visualizar' />
        </Buttons>
    );
}

const DateCell = ({ value, row: { original: attendance } }: CellProps<Attendance>) => {
    return iso2view(attendance.startedAt);
};

const DurationCell = ({ value, row: { original: attendance } }: CellProps<Attendance>) => {
    return diff2time(attendance.finishedAt, attendance.startedAt);
};

const AttendeeCell = ({ value, row: { original: attendance } }: CellProps<Attendance>) => {
    return <HStack g={1}>
        <Avatar url={attendance.user.avatar} w="25px" h='25px' />
        {attendance.user.name}
    </HStack>
};

const NpsCell = ({ value, row: { original: attendance } }: CellProps<Attendance>) => optional(attendance.npsNote);
const SituationCell = ({ value, row: { original: attendance } }: CellProps<Attendance>) => AttendanceSituationLabels[attendance.situation] || 'Não informado';

export const Table = observer(() => {

    const columns = [
        {
            Header: 'Data',
            accessor: 'startedAt',
            Cell: DateCell,
        },
        {
            Header: 'Duração',
            accessor: 'finishedAt',
            Cell: DurationCell,
        },
        {
            Header: 'Atendente',
            accessor: 'user.name',
            Cell: AttendeeCell
        },
        {
            Header: 'Solicitante',
            accessor: 'clientServed.name',
        },
        {
            Header: 'Situação',
            accessor: 'situation',
            Cell: SituationCell,
        },
        {
            Header: t('Actions'),
            accessor: 'id',
            Cell: actions,
        },
    ];

    const $paging = useRemotePaging();

    const { pageIndex, pageSize } = $paging;
    const filters = $onFilterChange
        .get()
        .toRequest();

    const { attendances: data, meta: paginated } = useAttendanceSearch({
        pageIndex: pageIndex.get(),
        pageSize: pageSize.get(),
        ...filters,
    });


    if (hasPerm([Perms.DeskConfNpsView])) {
        columns.splice(4, 0, {
            Header: 'NPS',
            accessor: 'npsNote',
            Cell: NpsCell,
        });
    }

    return <_Table
        {...{ columns, data, paginated }}
        onChangePage={pageIndex.set}
        onSelectPerPage={pageSize.set}
        isRemote
        isSortable
        pagination
    />
});


export const AllAttendancesTable = () => (
    <Suspense fallback={<TableSuspense columns={6} rows={12} />}>
        <Table />
    </Suspense>
)
